import { useMemo, useState, useEffect } from "react";
import { Button, Table, Badge, Form, Modal, Tab, Tabs } from "react-bootstrap";
import DashboardPagesTemplate from "../../Template/DashboardPagesTemplate";
import DashboardPageTitle from "../../Components/InnerPageTitle";
import { useTranslation } from "react-i18next";
import UserModal from "../../Components/UserManagement/UserModal";
import AccessModal from "../../Components/UserManagement/AccessModal";
import DeleteConfirmModal from "../../Components/UserManagement/DeleteConfirmModal";
import { useUserManagement } from "../../hookes/useUserManagement";
import { User as UserType } from "../../PropTypes/UserManagementTypes";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import "../../Assets/scss/components/user-management.scss";
import PaginationComponent from "../../Components/Pagination";
import { useSelector } from "react-redux";
import ShowRecordItem from "../../Components/ShowRecordItem";
import NoRecordFound from "../../Components/NoRecordFound";
import LodingImg from "../../Assets/img/icon-spinner.svg";
import { debounce } from "lodash";
import UserActivityPage from "../UserActivity";

// Import the UserActivity component

const UserManagement: React.FC = () => {
  const { t } = useTranslation();
  const {
    users,
    allPermission,
    saveUser,
    removeSubUser,
    fetchUserPermissions,
    savePermissionsForUser,
  } = useUserManagement();

  // Tab state
  const [activeTab, setActiveTab] = useState<string>("user-management");

  // local UI state
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [usersPerPage, setUsersPerPage] = useState(10);
  const [isUserLoading, setIsUserLoading] = useState(false);
  const { websiteList, user } = useSelector((store: StoreProptypes) => store);
  
  // modal state
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserType | null>(null);

  const [showAccessModal, setShowAccessModal] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserType | null>(null);

  // all websites modal (simple badge list)
  const [showAllWebsitesModal, setShowAllWebsitesModal] = useState(false);
  const [allWebsites, setAllWebsites] = useState<string[]>([]);
  const [allManageAccessWebsites, setManageAccessWebsites] = useState<string[]>(
    [],
  );
  const [disabledButton, setDisabledButton] = useState<boolean>(false);

  // Disabled Button Functions
  useEffect(() => {
    // check if admin user
    if (user && user?.department === 1) {
      setDisabledButton(true);
    } else {
      setDisabledButton(false);
    }
  }, [user]);

  // function to handle user search with loading
  const searchChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsUserLoading(true);
    setSearch(e.target.value);
    setCurrentPage(1);
    // Simulate loading delay for search
    setTimeout(() => {
      setIsUserLoading(false);
    }, 500);
  };

  // function to throttle search handler call on user input
  const debouncedSearchChangeHandler = useMemo(() => {
    return debounce(searchChangeHandler, 500);
  }, []);

  // Handle pagination with loading
  const handlePageChange = (page: number) => {
    setIsUserLoading(true);
    setCurrentPage(page);
    // Simulate loading delay for pagination
    setTimeout(() => {
      setIsUserLoading(false);
    }, 500);
  };

  // filtered users
  const filteredUsers = useMemo(
    () =>
      users.filter(
        (u) =>
          u.name.toLowerCase().includes(search.toLowerCase()) ||
          u.email.toLowerCase().includes(search.toLowerCase()),
      ),
    [users, search],
  );

  // pagination indices
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  const openInviteModal = (user?: UserType) => {
    setEditingUser(user || null);
    setShowUserModal(true);
  };

  const openAccessModal = async (user: UserType) => {
    setEditingUser(user);
    setShowAccessModal(true);
  };

  const confirmDelete = (user: UserType) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!userToDelete) return;
    await removeSubUser(userToDelete.id);
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  return (
    <DashboardPagesTemplate>
      <DashboardPageTitle
        pageTitle={t("User Management")}
        subPageTitle={t("Manage users and their website access")}
      />
      
      {/* Tabs Section */}
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k || "user-management")}
        className="mb-1 user-management-tabs"
        id="user-management-tabs"
      >
        {/* Tab 1: User Management */}
        <Tab eventKey="user-management" title={t("User Management")}>
          <div className="aioa_dashboard-order-list tour-user-management">
            <div
              className="aioa_dashboard-order-list-filter-wrapper"
              id="aioa_dashboard-user-management-filter"
            >
              {user?.department !== 1 && (
                <div className="aioa_dashboard-order-list-filter-block">
                  <Button
                    variant="primary"
                    disabled={disabledButton}
                    onClick={() => openInviteModal()}
                  >
                    {t("+ Invite User")}
                  </Button>
                </div>
              )}

              <div className="aioa_dashboard-order-list-filter-block">
                <Form.Control
                  id="userSearch"
                  type="text"
                  placeholder={t("Search users...")}
                  defaultValue={search}
                  onChange={debouncedSearchChangeHandler}
                  className="user-management-search w-100 w-md-auto"
                />
              </div>
            </div>

            <div className="aioa_dashboard-table tour-user-management">
              <div className="aioa_dashboard-table-thead">
                <div className="aioa_dashboard-table-tr">
                  <div className="aioa_dashboard-table-th tcw-100 aioa_dashboard-table-cell-sidegap">{t("Sr No")}</div>

                  <div className="aioa_dashboard-table-th tcw-200 aioa_dashboard-table-cell-sidegap">
                    {t("Full Name")}
                  </div>
                  <div className="aioa_dashboard-table-th tcw-300 aioa_dashboard-table-cell-sidegap">
                    {t("Email")}
                  </div>
                  <div className="aioa_dashboard-table-th tcw-auto aioa_dashboard-table-cell-sidegap">
                    {t("Websites")}
                  </div>
                  <div className="aioa_dashboard-table-th tcw-150 text-center aioa_dashboard-table-cell-sidegap">
                    {t("Actions")}
                  </div>
                </div>
              </div>

              {/* TBODY */}
              <div className="aioa_dashboard-table-body" aria-live="polite">
                {isUserLoading ? (
                  <div className="aioa_dashboard-loader d-flex justify-content-center m-0">
                    <img src={LodingImg} width="50px" alt="" />
                    <span className="visually-hidden">{t("Loading page. Please Do not refresh the page")} </span>
                  </div>
                ) : currentUsers.length === 0 ? (
                  <NoRecordFound
                    joyful={false}
                    imageType="domain"
                    noRecordText={`${t('No matching users')}.`}
                  />
                ) : (
                  currentUsers.map((u, index) => {
                    const srNo = indexOfFirstUser + index + 1;

                    return (
                      <div className="aioa_dashboard-table-tr" key={u.id}>

                        {/* SR NO */}
                        <div className="aioa_dashboard-table-td tcw-100 aioa_dashboard-table-cell-sidegap">
                          <div className="aioa_dashboard-table-td-cell-title d-xl-none">
                            {t("Sr No")}
                          </div>
                          <div className="aioa_dashboard-table-td-cell-value">
                            {srNo}
                          </div>
                        </div>

                        {/* Name */}
                        <div className="aioa_dashboard-table-td tcw-200 aioa_dashboard-table-cell-sidegap">
                          <div className="aioa_dashboard-table-td-cell-title d-xl-none">
                            {t("Full Name")}
                          </div>
                          <div className="aioa_dashboard-table-td-cell-value">
                            <strong>{u.name}</strong>
                          </div>
                        </div>

                        {/* Email */}
                        <div className="aioa_dashboard-table-td tcw-300 aioa_dashboard-table-cell-sidegap">
                          <div className="aioa_dashboard-table-td-cell-title d-xl-none">
                            {t("Email")}
                          </div>
                          <div className="aioa_dashboard-table-td-cell-value text-muted user-email">
                            {u.email}
                          </div>
                        </div>

                        {/* Websites */}
                        <div className="aioa_dashboard-table-td tcw-auto aioa_dashboard-table-cell-sidegap">
                          <div className="aioa_dashboard-table-td-cell-title d-xl-none">
                            {t("Websites")}
                          </div>
                          <div className="aioa_dashboard-table-td-cell-value website-badges-container">
                            {u.website?.length ? (
                              <div className="website-badges">
                                {u.website.slice(0, 2).map((w: any, i: number) => (
                                  <Badge key={i} bg="light" text="dark" className="me-1 mb-1 border website-badge">
                                    {w}
                                  </Badge>
                                ))}

                                {u.website.length > 2 && (
                                  <Badge
                                    bg="primary"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                      setAllWebsites(u.website || []);
                                      setShowAllWebsitesModal(true);
                                    }}
                                  >
                                    +{u.website.length - 2}
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted">{t("No access")}</span>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="aioa_dashboard-table-td tcw-150 text-center aioa_dashboard-table-cell-sidegap">
                          <div className="aioa_dashboard-table-td-cell-title d-xl-none">
                            {t("Actions")}
                          </div>
                          <div className="aioa_dashboard-table-td-cell-value d-flex align-items-center justify-content-center flex-nowrap gap-2">
                            {user?.department !== 1 && (
                              <Button
                                variant="outline-light text-primary"
                                size="sm"
                                className="btn btn-link me-2"
                                title="Edit"
                                onClick={() => openInviteModal(u)}
                              >
                                <i className="material-symbols-outlined">edit</i>
                              </Button>
                            )}

                            <Button
                              variant="outline-light text-primary"
                              size="sm"
                              className="btn btn-link me-2"
                              title="Access"
                              onClick={() => {
                                setManageAccessWebsites(u?.website || []);
                                openAccessModal(u);
                              }}
                            >
                              <i className="material-symbols-outlined">settings</i>
                            </Button>
                            {user?.department !== 1 && (
                              <Button
                                variant="outline-light text-danger"
                                size="sm"
                                className="btn btn-link"
                                title="Delete"
                                onClick={() => confirmDelete(u)}
                              >
                                <i className="material-symbols-outlined">delete</i>
                              </Button>
                            )}
                          </div>
                        </div>

                      </div>
                    );
                  })
                )}
              </div>
              {/* Pagination */}
              {!isUserLoading && currentUsers?.length > 0 && (
                <div className="aioa_dashboard-table-pagination-main">
                  <>
                    <ShowRecordItem
                      offset={indexOfFirstUser}
                      limit={usersPerPage}
                      totalRecord={filteredUsers.length}
                    />

                    <PaginationComponent
                      currentPage={currentPage}
                      totalRecords={filteredUsers.length}
                      itemsPerPage={usersPerPage}
                      setCurrentPage={handlePageChange}
                    />
                  </>
                </div>
              )}
            </div>
          </div>
        </Tab>
        
        {/* Tab 2: User Activity */}
        <Tab eventKey="user-activity" title={t("User Activity")}>
          <UserActivityPage />
        </Tab>
      </Tabs>

      <UserModal
        show={showUserModal}
        onHide={() => setShowUserModal(false)}
        websiteList={websiteList}
        allPermission={allPermission}
        editingUser={editingUser}
        onSave={async (payload, editingUserId, websites) => {
          return saveUser(payload, editingUserId, websites as any);
        }}
      />

      <AccessModal
        show={showAccessModal}
        onHide={() => setShowAccessModal(false)}
        websiteList={websiteList}
        allPermission={allPermission}
        editingUser={editingUser || undefined}
        disabledButton={disabledButton}
        fetchUserPermissions={fetchUserPermissions}
        onSaveAccess={savePermissionsForUser}
        allManageAccessWebsites={allManageAccessWebsites}
      />

      <DeleteConfirmModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        user={userToDelete || undefined}
        onConfirm={handleDeleteConfirmed}
      />

      {/* All Websites Modal */}
      <ModalAllWebsites
        show={showAllWebsitesModal}
        onHide={() => setShowAllWebsitesModal(false)}
        websites={allWebsites}
      />
    </DashboardPagesTemplate>
  );
};

export default UserManagement;

// Small inline component for modal showing all websites
type MAProps = { show: boolean; onHide: () => void; websites: string[] };
const ModalAllWebsites: React.FC<MAProps> = ({ show, onHide, websites }) => {
  const { t } = useTranslation();
  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t("All Websites")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {websites.map((w, idx) => (
          <Badge
            bg="light"
            text="dark"
            className="me-1 mb-1 border custom-badge"
            key={idx}
          >
            {w}
          </Badge>
        ))}
      </Modal.Body>
    </Modal>
  );
};